// frontend_react/src/components/AdminPanel/ClientListPanel.jsx

import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  getClients,
  updateClient,
  deleteClient as apiDeleteClient,
} from "../../services/adminApi";
import logger from "../../utils/logger";
import { useAdminScope } from "../../context/AdminScopeContext";
import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../pages/admin/ui_engine";
import { useAdminConfirmation } from "../../pages/admin/panel_engine/consumer";
import { AdminButton } from "../../pages/admin/action_engine";

import {
  AdminActions,
} from "../../pages/admin/section_engine";

export default function ClientListPanel() {
  const [clients, setClients] = useState([]);
  const [editingUuid, setEditingUuid] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const { selectedCompanyUuid } = useAdminScope();
  const confirm = useAdminConfirmation();

  const loadClients = async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await getClients({
        companyUuid: selectedCompanyUuid,
      });
      setClients(Array.isArray(data) ? data : data.clients || []);
    } catch (err) {
      logger.error(err);
      setError("Erreur lors du chargement des clients.");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (client) => {
    setEditingUuid(client.uuid);
    setDraft({
      firstname: client.firstname || "",
      lastname: client.lastname || "",
      email: client.email || "",
      phone_number: client.phone_number || "",
      address: client.address || "",
      postal_code: client.postal_code || "",
      city: client.city || "",
      country: client.country || "France",
    });
    setMessage("");
    setError("");
  };

  const cancelEdit = () => {
    setEditingUuid(null);
    setDraft({});
  };

  const handleDraftChange = (event) => {
    const { name, value } = event.target;
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const saveClient = async (uuid) => {
    setMessage("");
    setError("");

    try {
      await updateClient(uuid, draft, {
        companyUuid: selectedCompanyUuid,
      });
      setMessage("Client mis à jour.");
      setEditingUuid(null);
      setDraft({});
      await loadClients();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de la mise à jour du client."
      );
    }
  };

  const deleteClient = async (uuid) => {
    const confirmed = await confirm({
      title: "Supprimer le client ?",
      message: "Confirmer la suppression définitive de ce client ?",
      confirmLabel: "Supprimer",
      cancelLabel: "Conserver",
      intent: "danger",
    });

    if (!confirmed) return;

    setMessage("");
    setError("");

    try {
      await apiDeleteClient(uuid, {
        companyUuid: selectedCompanyUuid,
      });
      setMessage("Client supprimé.");
      await loadClients();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de la suppression du client."
      );
    }
  };

  useEffect(() => {
    setEditingUuid(null);
    setDraft({});
    loadClients();
  }, [selectedCompanyUuid]);

  const exportRows = useMemo(() => {
    return clients.map((client) => ({
      uuid: client.uuid || "",
      name: client.name || "",
      firstname: client.firstname || "",
      lastname: client.lastname || "",
      email: client.email || "",
      phone_number: client.phone_number || "",
      address: client.address || "",
      postal_code: client.postal_code || "",
      city: client.city || "",
      country: client.country || "",
    }));
  }, [clients]);

  const downloadCsv = () => {
    const headers = [
      "uuid",
      "name",
      "firstname",
      "lastname",
      "email",
      "phone_number",
      "address",
      "postal_code",
      "city",
      "country",
    ];

    const csv = [
      headers.join(";"),
      ...exportRows.map((row) =>
        headers
          .map((header) => {
            const value = String(row[header] ?? "");
            return `"${value.replaceAll('"', '""')}"`;
          })
          .join(";")
      ),
    ].join("\n");

    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `clients_queens360_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    const headers = [
      "UUID",
      "Nom complet",
      "Prénom",
      "Nom",
      "E-mail",
      "Téléphone",
      "Adresse",
      "Code postal",
      "Ville",
      "Pays",
    ];

    const rows = exportRows.map((client) => [
      client.uuid,
      client.name,
      client.firstname,
      client.lastname,
      client.email,
      client.phone_number,
      client.address,
      client.postal_code,
      client.city,
      client.country,
    ]);

    const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
        </head>
        <body>
          <table>
            <thead>
              <tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${rows
                .map(
                  (row) =>
                    `<tr>${row
                      .map((cell) => `<td>${String(cell ?? "")}</td>`)
                      .join("")}</tr>`
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `clients_queens360_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <AdminLoadingState message="Chargement des clients..." />
    );
  }

  return (
    <ClientListWrapper>
      {message && <PanelMessage>{message}</PanelMessage>}
      {error && (
        <AdminErrorState
          message={error}
          compact
        />
      )}

      <Toolbar>
        <AdminButton
          type="button"
          variant="ghost"
          fullWidth
          onClick={downloadCsv}
        >
          Export CSV
        </AdminButton>

        <AdminButton
          type="button"
          variant="success"
          fullWidth
          onClick={downloadExcel}
        >
          Export Excel
        </AdminButton>
      </Toolbar>

      {clients.length === 0 ? (
        <AdminEmptyState message="Aucun client trouvé." />
      ) : (
        <ClientCards>
          {clients.map((client) => {
            const isEditing = editingUuid === client.uuid;

            return (
              <ClientCard key={client.uuid}>
                <ClientTopLine>
                  <div>
                    <strong>{client.name || `${client.firstname} ${client.lastname}`}</strong>
                    <small>{client.email}</small>
                  </div>
                  <RoleBadge>Client</RoleBadge>
                </ClientTopLine>

                {isEditing ? (
                  <EditGrid>
                    <Field>
                      <label>Prénom</label>
                      <input
                        name="firstname"
                        value={draft.firstname}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>Nom</label>
                      <input
                        name="lastname"
                        value={draft.lastname}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>E-mail</label>
                      <input
                        name="email"
                        type="email"
                        value={draft.email}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>Téléphone</label>
                      <input
                        name="phone_number"
                        value={draft.phone_number}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>Adresse</label>
                      <input
                        name="address"
                        value={draft.address}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>Code postal</label>
                      <input
                        name="postal_code"
                        value={draft.postal_code}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>Ville</label>
                      <input
                        name="city"
                        value={draft.city}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <Field>
                      <label>Pays</label>
                      <input
                        name="country"
                        value={draft.country}
                        onChange={handleDraftChange}
                      />
                    </Field>

                    <AdminActions>
                      <AdminButton
                        type="button"
                        variant="success"
                        fullWidth
                        onClick={() => saveClient(client.uuid)}
                      >
                        Enregistrer
                      </AdminButton>
                      <AdminButton
                        type="button"
                        variant="ghost"
                        fullWidth
                        onClick={cancelEdit}
                      >
                        Annuler
                      </AdminButton>
                    </AdminActions>
                  </EditGrid>
                ) : (
                  <>
                    <ClientMeta>
                      <span>{client.phone_number || "Téléphone non renseigné"}</span>
                      <span>
                        {[client.postal_code, client.city].filter(Boolean).join(" ") ||
                          "Ville non renseignée"}
                      </span>
                      <span>{client.country || "Pays non renseigné"}</span>
                    </ClientMeta>

                    <UuidLine>{client.uuid}</UuidLine>

                    <AdminActions>
                      <AdminButton
                        type="button"
                        variant="ghost"
                        fullWidth
                        onClick={() => startEdit(client)}
                      >
                        Modifier
                      </AdminButton>
                      <AdminButton
                        type="button"
                        variant="danger"
                        fullWidth
                        onClick={() => deleteClient(client.uuid)}
                      >
                        Supprimer
                      </AdminButton>
                    </AdminActions>
                  </>
                )}
              </ClientCard>
            );
          })}
        </ClientCards>
      )}
    </ClientListWrapper>
  );
}

const ClientListWrapper = styled.div`
  color: #f7f7f7;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

const ClientCards = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const ClientCard = styled.article`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ClientTopLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;

  strong {
    display: block;
    font-weight: 950;
  }

  small {
    display: block;
    margin-top: 0.25rem;
    opacity: 0.7;
  }
`;

const RoleBadge = styled.span`
  height: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.75rem;
  font-weight: 950;
`;

const ClientMeta = styled.div`
  display: grid;
  gap: 0.35rem;
  margin-top: 0.9rem;
  font-size: 0.9rem;
  opacity: 0.82;
`;

const UuidLine = styled.div`
  margin-top: 0.75rem;
  font-family: monospace;
  font-size: 0.72rem;
  opacity: 0.45;
  word-break: break-all;
`;

const EditGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.8;
  }

  input {
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
  }
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: rgba(40, 140, 80, 0.9);
  font-weight: 850;
`;
